import {
  AdjustmentsIcon,
  ArrowsExpandIcon,
  CameraIcon,
  ChartBarIcon,
  ChatAlt2Icon,
  ChatAltIcon,
  ClockIcon,
  CogIcon,
  ColorSwatchIcon,
  CreditCardIcon,
  CubeTransparentIcon,
  FilmIcon,
  FingerPrintIcon,
  GiftIcon,
  HeartIcon,
  HomeIcon,
  IdentificationIcon,
  LightningBoltIcon,
  MicrophoneIcon,
  MusicNoteIcon,
  PhotographIcon,
  PuzzleIcon,
  QuestionMarkCircleIcon,
  ScissorsIcon,
  SparklesIcon,
  SpeakerphoneIcon,
  StatusOnlineIcon,
  SwitchHorizontalIcon,
  TemplateIcon,
  TranslateIcon,
  UserCircleIcon,
  UserGroupIcon,
  UsersIcon,
  VideoCameraIcon,
  ViewGridAddIcon,
  ViewGridIcon,
  VolumeUpIcon,
} from '@heroicons/react/outline';
import type { ComponentType } from 'react';

import { CATEGORY_META } from '@/components/presets/catalog/catalogData';

import {
  getPresetToolHref,
  type PresetCategory,
  presets,
} from '@/constants/presets';
import { toolCategories } from '@/constants/toolCategories';

export type NavIcon = ComponentType<{ className?: string }>;

/** Small tags after a label, as in `toolCategories` (see NavItemBadge). */
export type NavBadgeKind = 'new' | 'beta';

export interface NavItem {
  /** A key of the `dashboard` namespace, unless `translate` is false. */
  name: string;
  href: string;
  icon: NavIcon;
  /** The favorites id. /presets and the dashboard skip these slugs in "Your tools". */
  slug: string;
  badge?: NavBadgeKind;
  /** Text tools show their English title, as everywhere outside /presets. */
  translate?: boolean;
  /** Home and Tools: always shown, never pinned. */
  cannotUnpin?: boolean;
}

/**
 * Every app the sidebar can pin, in sidebar order. Icons are the /presets
 * catalogue's (`APPS` in catalogData.ts), one heroicons set, except where two
 * pinned apps would have looked the same. The slugs are the favorites ids:
 * /presets and the dashboard read this list to tell sidebar pins from tools,
 * so keep slugs stable.
 */
export const navigation: NavItem[] = [
  {
    name: 'Home',
    href: '/dashboard',
    icon: HomeIcon,
    slug: 'home',
    cannotUnpin: true,
  },
  {
    name: 'Tools',
    href: '/presets',
    icon: ViewGridIcon,
    slug: 'tools',
    cannotUnpin: true,
  },
  {
    name: 'Marky Agent',
    href: '/chat',
    icon: ChatAlt2Icon,
    slug: 'chat',
  },
  {
    name: 'Bots',
    href: '/agents',
    icon: ChatAltIcon,
    slug: 'bots',
  },
  {
    name: 'Workflows',
    href: '/workflows',
    icon: ViewGridAddIcon,
    slug: 'workflows',
  },
  {
    name: 'AI Images',
    href: '/ai-images',
    icon: PhotographIcon,
    slug: 'ai-images',
  },
  {
    name: 'Sprite Generator',
    href: '/sprite-generator',
    icon: PuzzleIcon,
    slug: 'sprite-generator',
  },
  {
    name: 'AI Image Editor',
    href: '/ai-image-editor',
    icon: AdjustmentsIcon,
    slug: 'ai-image-editor',
  },
  {
    name: 'AI Video Editor',
    href: '/ai-video-editor',
    icon: ScissorsIcon,
    slug: 'ai-video-editor',
  },
  {
    name: 'AI Videos',
    href: '/ai-videos',
    icon: VideoCameraIcon,
    slug: 'ai-videos',
  },
  {
    name: 'AI Talking Videos',
    href: '/ai-talking-videos',
    icon: UserCircleIcon,
    slug: 'ai-talking-videos',
  },
  {
    name: 'AI Text to Speech',
    href: '/tts',
    icon: VolumeUpIcon,
    slug: 'tts',
  },
  {
    name: 'AI Speech to Text',
    href: '/audios',
    icon: MicrophoneIcon,
    slug: 'audios',
  },
  {
    name: 'Record & Transcribe',
    href: '/note-taker',
    icon: StatusOnlineIcon,
    slug: 'note-taker',
  },
  {
    name: 'AI Music Generator',
    href: '/music-generator',
    icon: MusicNoteIcon,
    slug: 'music-generator',
  },
  {
    name: 'AI Interior Designer',
    href: '/ai-interior-designer',
    icon: ColorSwatchIcon,
    slug: 'ai-interior-designer',
  },
  {
    name: 'AI Photo Studio',
    href: '/ai-images/models',
    icon: CameraIcon,
    slug: 'ai-images-models',
  },
  {
    name: 'Headshots',
    href: '/headshots',
    icon: IdentificationIcon,
    slug: 'headshots',
  },
  {
    name: 'Brand Voice',
    href: '/voices',
    icon: FingerPrintIcon,
    slug: 'voices',
  },
  {
    name: 'Magic Upscaler',
    href: '/magic-upscaler',
    icon: ArrowsExpandIcon,
    slug: 'magic-upscaler',
  },
  {
    name: 'AI Audio Generator',
    href: '/audio-generator',
    icon: SpeakerphoneIcon,
    slug: 'audio-generator',
  },
  {
    name: 'Sound Effect Generator',
    href: '/sound-effects',
    icon: LightningBoltIcon,
    slug: 'sound-effects',
  },
  {
    name: 'Remove Background',
    href: '/ai-images?action=remove-background',
    icon: CubeTransparentIcon,
    slug: 'remove-background',
  },
  {
    name: 'Replace Background',
    href: '/ai-images?action=replace-background',
    icon: TemplateIcon,
    slug: 'replace-background',
  },
];

export const HOME_ITEM = navigation[0];
export const TOOLS_ITEM = navigation[1];
export const HISTORY_ITEM: NavItem = {
  name: 'History',
  href: '/history',
  icon: ClockIcon,
  slug: 'history',
};
/** Launched 2026-10-23: in the top group for every signed-in user, not a pin. */
export const EMPLOYEES_ITEM: NavItem = {
  name: 'AI Employees',
  href: '/employees',
  icon: UserGroupIcon,
  slug: 'employees',
  badge: 'new',
};

/** The top group for signed-in users. Never listed again under Pinned. */
export const PRIMARY_ITEMS = [
  HOME_ITEM,
  TOOLS_ITEM,
  HISTORY_ITEM,
  EMPLOYEES_ITEM,
];

/** Always on screen, so the Tools flyout shows no star for them. */
export const ALWAYS_SHOWN_SLUGS = new Set(
  PRIMARY_ITEMS.map((item) => item.slug),
);

/** The apps a signed-out visitor sees under "Apps": everything but Home and Tools. */
export const APP_ITEMS = navigation.filter((item) => !item.cannotUnpin);

const NAV_SLUGS = new Set(navigation.map((item) => item.slug));

/**
 * Tools the Tools flyout can star that are neither a sidebar app nor a text
 * tool (Video Agent, Voice Changer, the video model shortcuts...). Their stars
 * used to light up without anything appearing in the sidebar.
 */
const EXTRA_ICONS: Record<string, NavIcon> = {
  'video-agent': FilmIcon,
  'voice-changer': SwitchHorizontalIcon,
  dubbing: TranslateIcon,
  'motion-control': VideoCameraIcon,
  'extend-video': FilmIcon,
  'edit-video': FilmIcon,
};
const EXTRA_ITEMS = new Map<string, NavItem>();
for (const category of toolCategories) {
  for (const tool of category.tools) {
    if (
      NAV_SLUGS.has(tool.slug) ||
      ALWAYS_SHOWN_SLUGS.has(tool.slug) ||
      EXTRA_ITEMS.has(tool.slug)
    )
      continue;
    EXTRA_ITEMS.set(tool.slug, {
      name: tool.name,
      href: tool.href,
      icon: EXTRA_ICONS[tool.slug] ?? SparklesIcon,
      slug: tool.slug,
      badge: tool.badge,
    });
  }
}

/** At most this many text tools are pinned in the sidebar, as before. */
const MAX_PINNED_TOOLS = 10;

/** The sidebar's pins from the favorites list: apps, then other tools, then text tools. */
export function pinnedItems(favoriteTools: string[]): NavItem[] {
  if (!favoriteTools.length) return [];
  const favorites = new Set(favoriteTools);
  const apps = APP_ITEMS.filter((item) => favorites.has(item.slug));
  const extras = favoriteTools
    .map((slug) => EXTRA_ITEMS.get(slug))
    .filter((item): item is NavItem => !!item);
  const textTools: NavItem[] = [];
  for (const slug of favoriteTools) {
    if (textTools.length >= MAX_PINNED_TOOLS) break;
    if (NAV_SLUGS.has(slug) || ALWAYS_SHOWN_SLUGS.has(slug)) continue;
    if (EXTRA_ITEMS.has(slug)) continue;
    const preset = presets.find((p) => p.slug === slug);
    if (!preset) continue;
    const category = (
      Array.isArray(preset.category) ? preset.category[0] : preset.category
    ) as PresetCategory;
    textTools.push({
      name: preset.title,
      href: getPresetToolHref(preset),
      icon: CATEGORY_META[category]?.icon ?? CATEGORY_META.Other.icon,
      slug: preset.slug,
      translate: false,
    });
  }
  return [...apps, ...extras, ...textTools];
}

export interface AccountLink {
  /** A key of the `dashboard` namespace. */
  name: string;
  href: string;
  icon: NavIcon;
}

/** The account menu, in two groups. "My Profile" was another name for Settings. */
export const ACCOUNT_LINKS: AccountLink[][] = [
  [
    { name: 'Settings', href: '/settings', icon: CogIcon },
    { name: 'Subscription', href: '/settings/billing', icon: CreditCardIcon },
    { name: 'Usage', href: '/settings/usage', icon: ChartBarIcon },
    { name: 'My Team', href: '/settings/team', icon: UsersIcon },
  ],
  [
    { name: 'Favourites', href: '/history/favorites', icon: HeartIcon },
    { name: 'Refer a friend', href: '/settings/referrals', icon: GiftIcon },
    {
      name: 'Help & Support',
      href: '/support',
      icon: QuestionMarkCircleIcon,
    },
  ],
];

/** True on a page the account menu links to: the account button shows it. */
export function isAccountPath(path: string) {
  return /^\/(settings|support)(\/|$)/.test(path);
}

/**
 * The href among `hrefs` that the current page belongs to: the longest path
 * that equals the page's path or is a parent of it, and an href with a query
 * (`/ai-images?action=remove-background`) only when the page has that query.
 * One item is active at a time, so /ai-images/models lights up Photo Studio
 * and not AI Images as well.
 */
export function activeHref(asPath: string, hrefs: string[]): string | null {
  const [path, query = ''] = asPath.split('#')[0].split('?');
  const params = new URLSearchParams(query);
  let best: string | null = null;
  let bestScore = -1;
  for (const href of hrefs) {
    const [hrefPath, hrefQuery] = href.split('?');
    const pathMatches =
      path === hrefPath ||
      (hrefPath !== '/dashboard' && path.startsWith(`${hrefPath}/`));
    if (!pathMatches) continue;
    let score = hrefPath.length * 4 + (path === hrefPath ? 1 : 0);
    if (hrefQuery) {
      const wanted = new URLSearchParams(hrefQuery);
      let matches = true;
      wanted.forEach((value, key) => {
        if (params.get(key) !== value) matches = false;
      });
      if (!matches) continue;
      score += 2;
    }
    if (score > bestScore) {
      best = href;
      bestScore = score;
    }
  }
  return best;
}
